import { useEffect, useMemo, useState } from 'react'
import { videoApi } from '@carrent/api'
import { Icon } from '@carrent/ui'
import { formatDateTime, resetVideoSettingsCache } from '@carrent/domain'

/**
 * Настройки модуля «Видеозвонки» — секция на странице настроек владельца.
 *
 * Устроена как legal-секция: загрузка → форма → dirty → сохранение
 * одним PUT → сброс клиентского кэша публичных настроек. Отличие —
 * почти все поля булевы, поэтому вместо списка инпутов — группы
 * переключателей по макету плана.
 *
 * Зависимые настройки блокируются, а не прячутся: владелец должен
 * видеть, ЧТО станет доступно, если включить родителя.
 */

const GROUPS = [
  {
    title: 'Кто создаёт звонки',
    items: [
      { key: 'managers_can_create', label: 'Менеджеры могут создавать звонки',
        hint: 'Администратор может всегда.' },
      { key: 'drivers_can_create', label: 'Водители могут начинать звонок',
        hint: 'Быстрый видеоосмотр с места: водитель зовёт диспетчера.' },
      { key: 'guest_access', label: 'Гости без аккаунта по ссылке',
        hint: 'Страховой комиссар, арендодатель, кандидат — по персональной ссылке.' },
    ],
  },
  {
    title: 'Поведение звонка',
    items: [
      { key: 'prejoin_enabled', label: 'Пре-экран перед входом',
        hint: 'Проверка камеры и микрофона; гость представляется здесь.' },
      { key: 'lobby_for_guests', label: 'Гости всегда через лобби',
        hint: 'Модератор одобряет вход даже в открытых звонках.' },
      { key: 'chat_enabled', label: 'Чат внутри звонка',
        hint: 'Не сохраняется после завершения — для истории есть корпоративный чат.' },
      { key: 'screenshare_enabled', label: 'Демонстрация экрана' },
      { key: 'screenshare_guests', label: '… в том числе гостям',
        needs: 'screenshare_enabled' },
    ],
  },
  {
    title: 'Жизнь ссылки',
    items: [
      { key: 'auto_expire_after_end', label: 'Гасить ссылку после выхода последнего участника' },
      { key: 'single_use_invites', label: 'Гостевые приглашения одноразовые',
        hint: 'Приглашение сгорает после первого входа.' },
    ],
  },
  {
    title: 'Интеграции',
    items: [
      { key: 'call_from_chat', label: 'Кнопка звонка в диалогах чата' },
      { key: 'call_from_trip', label: '«Видеоосмотр» на странице поездки',
        hint: 'Диспетчер смотрит машину камерой водителя; звонок привязан к поездке.' },
      { key: 'call_from_ticket', label: 'Кнопка звонка в тикете' },
      { key: 'push_on_invite', label: 'Push-уведомление приглашённым', needsPush: true,
        hint: 'Web push о приглашении тем, у кого приложение закрыто.' },
    ],
  },
]

const PRIVACY_OPTIONS = [
  { value: 'open', label: 'Открытая ссылка', hint: 'Подключится любой, у кого есть ссылка' },
  { value: 'password', label: 'По паролю', hint: 'Ссылка плюс пароль' },
  { value: 'invite', label: 'По приглашению', hint: 'Персональные ссылки; остальные — в лобби' },
]

const NUMBERS = [
  { key: 'default_ttl_hours', label: 'Срок действия ссылки, часов', min: 1, max: 168 },
  { key: 'start_muted_after', label: 'Входящие после N-го — без микрофона', min: 0, max: 100,
    hint: '0 — выключено.' },
  { key: 'max_participants', label: 'Максимум участников', min: 2, max: 50 },
]

export default function VideoSettingsSection() {
  const [form, setForm] = useState(null)
  const [initial, setInitial] = useState(null)
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null, jitsi_configured: false, push_configured: true })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      const { data } = await videoApi.adminSettings()
      const { updated_at, updated_by, defaults: _d, jitsi_configured, push_configured, ...values } = data.data
      setForm(values)
      setInitial(values)
      setMeta({ updated_at, updated_by, jitsi_configured, push_configured })
      void _d
    } catch (e) {
      setError(e.response?.data?.message || 'Не удалось загрузить настройки видеозвонков')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const dirty = useMemo(
    () => Boolean(form && initial) && JSON.stringify(form) !== JSON.stringify(initial),
    [form, initial]
  )

  const setBool = (key) => (e) => {
    setSaved(false)
    setForm((f) => ({ ...f, [key]: e.target.checked }))
  }

  const setNumber = (key, min, max) => (e) => {
    setSaved(false)
    const raw = parseInt(e.target.value, 10)
    const value = Number.isNaN(raw) ? min : Math.min(max, Math.max(min, raw))
    setForm((f) => ({ ...f, [key]: value }))
  }

  const save = async () => {
    setSaving(true)
    setError('')
    try {
      // recording_enabled сервер пока не принимает включённым;
      // отправляем всё остальное как есть — контракт закрытый.
      const { data } = await videoApi.updateSettings(form)
      const { updated_at, updated_by: _u, defaults: _d2, jitsi_configured: _j, ...values } = data.data
      void _u; void _d2; void _j
      setForm(values)
      setInitial(values)
      setMeta((m) => ({ ...m, updated_at }))
      // Публичный срез кэшируется на сеанс — после правки кэш обязан
      // протухнуть, иначе кнопки звонков в этой вкладке не изменятся.
      resetVideoSettingsCache()
      setSaved(true)
    } catch (e) {
      setError(e.response?.data?.message || 'Не удалось сохранить')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <section className="card flex-col gap-sm">
        <h2 className="section-title">Видеозвонки</h2>
        <span className="skeleton table__skeleton-row" />
        <span className="skeleton table__skeleton-row" />
      </section>
    )
  }

  if (!form) {
    return (
      <section className="card flex-col gap-sm">
        <h2 className="section-title">Видеозвонки</h2>
        {error && <div className="alert alert--error">{error}</div>}
        <button type="button" className="btn btn--secondary" onClick={load}>
          Повторить
        </button>
      </section>
    )
  }

  const row = (item) => {
    const blocked = (item.needs && !form[item.needs])
      || (item.needsPush && !meta.push_configured)
    return (
      <label className={`settings__row ${blocked ? "video-settings-off" : ""}`} key={item.key}>
        <span className="settings__text">
          <span className="settings__label">{item.label}</span>
          {item.hint && <span className="settings__hint">{item.hint}</span>}
          {item.needsPush && !meta.push_configured && (
            <span className="settings__hint">
              Пуши не настроены (пустые VAPID-ключи) — заполните их в .env,
              чтобы включить.
            </span>
          )}
        </span>
        <input
          type="checkbox"
          className="switch"
          checked={!!form[item.key] && !blocked}
          disabled={blocked}
          onChange={setBool(item.key)}
        />
      </label>
    )
  }

  return (
    <section className="card flex-col gap">
      <div>
        <h2 className="section-title">Видеозвонки</h2>
        <p className="text-muted video-hint">
          Планёрки, видеоосмотр повреждений, звонки из чата. Работает на
          собственном видеосервере — данные не покидают инфраструктуру.
        </p>
      </div>

      {!meta.jitsi_configured && (
        <div className="alert alert--error">
          Видеосервер не настроен (пустой JITSI_APP_SECRET). Модуль можно
          включить, но звонки будут отвечать «сервис недоступен», пока стек
          Jitsi не развёрнут — см. DEPLOYMENT.md.
        </div>
      )}

      {/* Мастер-тумблер: выключен — всё остальное неактуально, но видно. */}
      <label className="settings__row">
        <span className="settings__text">
          <span className="settings__label">Включить модуль</span>
          <span className="settings__hint">
            Выключенный модуль прячет разделы звонков у всех и закрывает API.
          </span>
        </span>
        <input
          type="checkbox"
          className="switch"
          checked={!!form.enabled}
          onChange={setBool('enabled')}
        />
      </label>

      <fieldset
        className={`flex-col gap video-fieldset ${form.enabled ? '' : 'video-fieldset--off'}`}
        disabled={!form.enabled}
      >
        <div className="flex-col gap-sm">
          <h3 className="section-title video-settings-sub">
            Приватность по умолчанию
          </h3>
          {PRIVACY_OPTIONS.map((o) => (
            <label className="settings__row" key={o.value}>
              <span className="settings__text">
                <span className="settings__label">{o.label}</span>
                <span className="settings__hint">{o.hint}</span>
              </span>
              <input
                type="radio"
                name="video-default-privacy"
                checked={form.default_privacy === o.value}
                onChange={() => {
                  setSaved(false)
                  setForm((f) => ({ ...f, default_privacy: o.value }))
                }}
              />
            </label>
          ))}
        </div>

        {GROUPS.map((g) => (
          <div className="flex-col gap-sm" key={g.title}>
            <h3 className="section-title video-settings-sub">{g.title}</h3>
            {g.items.map(row)}
          </div>
        ))}

        <div className="flex-col gap-sm">
          <h3 className="section-title video-settings-sub">Ограничения</h3>
          {NUMBERS.map((n) => (
            <div className="field" key={n.key}>
              <label className="field__label" htmlFor={`video-${n.key}`}>{n.label}</label>
              <input
                id={`video-${n.key}`}
                className="input video-form-narrow"
                type="number"
                min={n.min}
                max={n.max}
                value={form[n.key] ?? n.min}
                onChange={setNumber(n.key, n.min, n.max)}
              />
              {n.hint && <span className="field__hint">{n.hint}</span>}
            </div>
          ))}
        </div>

        <label className="settings__row">
          <span className="settings__text">
            <span className="settings__label">Запись звонков</span>
            <span className="settings__hint">
              Кнопка записи у модераторов; файлы — в объектном хранилище,
              ссылки на карточке звонка. Требует контейнера Jibri в стеке
              Jitsi (deploy/jitsi, ~4 ГБ RAM на одну запись).
            </span>
          </span>
          <input
            type="checkbox"
            className="switch"
            checked={!!form.recording_enabled}
            onChange={setBool('recording_enabled')}
          />
        </label>
      </fieldset>

      {error && <div className="alert alert--error">{error}</div>}
      {saved && !dirty && <div className="alert alert--success">Настройки сохранены</div>}

      <div className="flex-row gap-sm video-actions">
        <button
          type="button"
          className="btn btn--primary"
          onClick={save}
          disabled={saving || !dirty}
        >
          {saving ? <span className="spinner spinner--sm" /> : <Icon name="check" size={14} />}
          Сохранить
        </button>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => { setSaved(false); setForm({ ...initial }) }}
          disabled={!dirty || saving}
        >
          Отменить
        </button>
      </div>

      {meta.updated_at && (
        <p className="text-muted video-hint">
          Изменено {formatDateTime(meta.updated_at)}
          {meta.updated_by ? `, ${meta.updated_by.full_name}` : ''}. Правки
          записываются в журнал.
        </p>
      )}
    </section>
  )
}
